import { AgGrid } from "@jsq/ds/core";
import { GetBankAccountsForConfigurationQuery } from "#graphql/supergraphTypes";
import { toDbId } from "#utils/graphql_utils";
import {
  BankAccountRowItem,
  InvestmentEntity,
  ProviderInfo,
  VerificationInfo,
  ConfigurationInfo,
} from "./types";

const FIDES_PROVIDER_NAME = "FIDES";
const RPA_PROVIDER_NAME = "RPA";

const hasProvider = (
  providers: Array<{ providerId: number; providerName: string }> | null | undefined,
  providerName: string,
): boolean => {
  if (!providers) return false;
  return providers.some((p) => p.providerName.toUpperCase() === providerName.toUpperCase());
};

// FundRecsExportStatus enum values: DISABLED = 0, ENABLED = 1
const FUND_RECS_ENABLED_STATUS = 1;

const hasFundRecsEnabled = (
  configurations:
    | Array<{ providerCapabilityId: number | null; fundRecsExportStatus: number }>
    | null
    | undefined,
): boolean => {
  if (!configurations) return false;
  return configurations.some((c) => c.fundRecsExportStatus === FUND_RECS_ENABLED_STATUS);
};

const getInvestmentEntities = (
  investmentEntities: Array<{ id: string; name: string }> | null | undefined,
): InvestmentEntity[] => {
  if (!investmentEntities) return [];
  return investmentEntities.map((e) => ({
    entityId: toDbId(e.id),
    entityName: e.name,
  }));
};

const getProviders = (
  providers:
    | Array<{ providerId: number; providerName: string; bankSource?: string }>
    | null
    | undefined,
): ProviderInfo[] => {
  if (!providers) return [];
  return providers.map((p) => ({
    providerId: p.providerId,
    providerName: p.providerName as ProviderInfo["providerName"],
    bankSource: p.bankSource || "",
  }));
};

const getVerifications = (
  verifications: Array<{ providerId: number; verificationStatus: string }> | null | undefined,
): VerificationInfo[] => {
  if (!verifications) return [];
  return verifications.map((v) => ({
    providerId: v.providerId,
    verificationStatus: v.verificationStatus,
  }));
};

const getConfigurations = (
  configurations:
    | Array<{
        providerCapabilityId: number | null;
        fundRecsExportStatus: number;
        fundRecsUdf?: string | null;
      }>
    | null
    | undefined,
): ConfigurationInfo[] => {
  if (!configurations) return [];
  return configurations.map((c) => ({
    providerCapabilityId: c.providerCapabilityId,
    fundRecsExportStatus: c.fundRecsExportStatus,
    fundRecsUdf: c.fundRecsUdf ?? null,
  }));
};

export const toLoadBankAccountsSuccessParams = (
  data: GetBankAccountsForConfigurationQuery,
): { resp: AgGrid.LoadSuccessParams } => {
  const edges = data.bankAccountsConnection?.edges || [];

  const rowData: BankAccountRowItem[] = edges.map((edge) => {
    const node = edge?.node;

    // Get investment entities (primary entities linked via Account.bank_account_id)
    const investmentEntities = getInvestmentEntities(node?.investmentEntities);
    // Get mapped investment entities (reporting-only)
    const mappedEntities = getInvestmentEntities(node?.mappedInvestmentEntities);

    return {
      id: node?.id || "",
      bankAccountId: node?.id ? toDbId(node.id) : 0,
      bankAccountName: node?.bankAccountName || "",
      bankName: node?.bankName || "",
      bankInfoRedacted: node?.bankInfoRedacted || "",
      investmentEntities,
      mappedInvestmentEntities: mappedEntities,
      providers: getProviders(node?.providers),
      verifications: getVerifications(node?.verifications),
      configurations: getConfigurations(node?.configurations),
      fidesEnabled: hasProvider(node?.providers, FIDES_PROVIDER_NAME),
      rpaEnabled: hasProvider(node?.providers, RPA_PROVIDER_NAME),
      fundRecsEnabled: hasFundRecsEnabled(node?.configurations),
    };
  });

  return {
    resp: {
      rowData,
      rowCount: data.bankAccountsConnection?.totalCount || 0,
    },
  };
};
